// Dependency-free grouping/summarising logic for the drink history calendar.
// Deliberately has no React/AntD/dayjs imports so it can be unit tested in isolation.
import { DrinkActivitySummary } from "../../../services/drinkService";

export type DaySummary = {
    /** Local-time day key in YYYY-MM-DD form, see getLocalDayKey. */
    dayKey: string;
    /** Local midnight for this day, handy for calendar cell lookups. */
    date: Date;
    /** Entries for the day, sorted ascending by timestamp. */
    entries: DrinkActivitySummary[];
    /** Sum of amountMl across all entries for the day. */
    totalMl: number;
};

/**
 * Derives a local-time day key from a Date using getFullYear/getMonth/getDate.
 *
 * We deliberately do NOT slice an ISO string (date.toISOString().slice(0, 10)) —
 * that reads UTC fields and would bucket a drink logged at, say, 23:30 local time
 * into the next UTC day. Reading the local getters keeps the bucket aligned with
 * the viewer's own calendar day.
 */
export const getLocalDayKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

/**
 * Groups drink activities by local calendar day. Activities without a timestamp
 * are skipped (there is no day to bucket them into). Input order does not matter;
 * entries within each day are returned sorted ascending by timestamp.
 */
export const groupActivitiesByDay = (
    activities: DrinkActivitySummary[]
): Map<string, DaySummary> => {
    const byDay = new Map<string, DaySummary>();

    activities.forEach((activity) => {
        if (!activity.timestamp) {
            return;
        }
        const dayKey = getLocalDayKey(activity.timestamp);
        let summary = byDay.get(dayKey);
        if (!summary) {
            summary = {
                dayKey,
                date: new Date(
                    activity.timestamp.getFullYear(),
                    activity.timestamp.getMonth(),
                    activity.timestamp.getDate()
                ),
                entries: [],
                totalMl: 0,
            };
            byDay.set(dayKey, summary);
        }
        summary.entries.push(activity);
        summary.totalMl += activity.amountMl;
    });

    byDay.forEach((summary) => {
        summary.entries.sort(
            (a, b) => (a.timestamp?.getTime() ?? 0) - (b.timestamp?.getTime() ?? 0)
        );
    });

    return byDay;
};
